import { useEffect, useState, useCallback, useRef } from "react";
import { RefreshCw, CheckCircle2, XCircle, Trash2, Play, Rocket } from "lucide-react";
import { toast } from "sonner";
import { ssStatus, ssSync, ssStaged, ssSetApproval, ssPromote, ssReset, ssRetryFailed, ssApproveAll } from "../../lib/api";
import useSyncSpeed from "../../hooks/useSyncSpeed";

// iter81 — desktop notification helper (mirrored from SanmarSyncTab).
// Pings the admin when a long S&S sync completes so they can walk away
// during the 5-10 min pull-and-normalize pipeline.
function notifyDesktop(title, body) {
  try {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    if (typeof document !== "undefined" && document.visibilityState === "visible") return;
    const n = new Notification(title, {
      body, icon: "/favicon.ico", tag: "ss-sync", renotify: true,
    });
    n.onclick = () => { try { window.focus(); n.close(); } catch { /* noop */ } };
  } catch { /* noop */ }
}

async function requestDesktopNotifyPermission() {
  try {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission === "default") {
      await Notification.requestPermission();
    }
  } catch { /* noop */ }
}

/**
 * S&S Activewear (Canada) sync tab — iter79.
 *
 * Flow mirrors the SanMar sync UX but leaner:
 *   1. RESET  → wipe all live S&S products + staging (typed DELETE confirmation)
 *   2. SYNC   → fetches all 1,090 styles from api-ca.ssactivewear.com and
 *               stores them in `db.ss_products` for review
 *   3. APPROVE→ per-style checkbox in the staging table
 *   4. PROMOTE→ writes approved staging docs into `db.products` with
 *               source="ssactivewear" (customizable, drafts by default)
 *
 * Progress is polled from `/api/admin/ss/status` every 2 s while a sync
 * is running.
 */
export default function SSSyncTab({ pw }) {
  const [status, setStatus] = useState(null);
  const [staged, setStaged] = useState([]);
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("");
  const [approvedFilter, setApprovedFilter] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [brandOptions, setBrandOptions] = useState([]);
  const [priceMode, setPriceMode] = useState("retail");
  const [markupPct, setMarkupPct] = useState(0);
  const [overwrite, setOverwrite] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [s, list] = await Promise.all([
        ssStatus(pw),
        ssStaged({ q, brand, approved: approvedFilter || undefined, limit: 2000 }, pw),
      ]);
      setStatus(s);
      setStaged(list);
      // Refresh brand options when we have data. Only recompute when
      // there's no brand filter active so the dropdown shows every brand.
      if (!brand) {
        const setB = new Set();
        for (const it of list) if (it.brand_name) setB.add(it.brand_name);
        setBrandOptions(Array.from(setB).sort());
      }
    } catch (e) {
      // swallow — network hiccups shouldn't blow up the UI
    }
  }, [pw, q, brand, approvedFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  // iter81 — desktop notification on sync completion. We diff the phase
  // against the previous poll: transition from a non-terminal phase to
  // "done"/"error" fires a native notification (silent if tab is
  // visible or permission not granted).
  const prevPhaseRef = useRef(null);
  useEffect(() => {
    const cur = status?.last_run?.phase;
    const prev = prevPhaseRef.current;
    prevPhaseRef.current = cur;
    if (!cur) return;
    const isTerminal = (p) => p === "done" || p === "error";
    if (prev && !isTerminal(prev) && isTerminal(cur)) {
      const lr = status.last_run || {};
      if (cur === "done") {
        notifyDesktop(
          "S&S sync complete",
          `${lr.styles_done ?? 0}/${lr.styles_total ?? 0} styles synced${lr.styles_failed ? ` · ${lr.styles_failed} failed` : ""}`
        );
      } else {
        notifyDesktop("S&S sync failed", lr.error || "unknown error — check the recent runs log");
      }
    }
  }, [status?.last_run?.phase, status?.last_run]);

  // Poll every 2s while a sync is in progress.
  useEffect(() => {
    const active = status?.last_run?.phase && !["done", "error"].includes(status.last_run.phase);
    if (!active) return;
    const t = setInterval(refresh, 2000);
    return () => clearInterval(t);
  }, [status, refresh]);

  const doReset = async () => {
    const typed = window.prompt("This will DELETE all S&S products + staging. Type DELETE to confirm:");
    if ((typed || "").toUpperCase() !== "DELETE") return;
    try {
      const res = await ssReset(pw);
      toast.success(`Deleted ${res.products_deleted} live + ${res.staging_deleted} staged`);
      refresh();
    } catch { toast.error("Reset failed"); }
  };

  const doSync = async () => {
    if (syncing) return;
    setSyncing(true);
    requestDesktopNotifyPermission();
    try {
      const res = await ssSync({}, pw);
      toast.success(`Sync queued — ${res.run_id}`);
      // Kick a first poll immediately then rely on the interval effect.
      setTimeout(refresh, 800);
    } catch { toast.error("Sync failed to start"); }
    setSyncing(false);
  };

  const doRetryFailed = async () => {
    if (retrying) return;
    setRetrying(true);
    requestDesktopNotifyPermission();
    try {
      const res = await ssRetryFailed(pw);
      toast.success(`Retry queued — ${res.run_id}`);
      setTimeout(refresh, 800);
    } catch { toast.error("Retry failed to start"); }
    setRetrying(false);
  };

  const toggleApproval = async (styleId, cur) => {
    try {
      await ssSetApproval(styleId, !cur, pw);
      setStaged((arr) => arr.map((s) => s.style_id === styleId ? { ...s, approved: !cur } : s));
    } catch { toast.error("Update failed"); }
  };

  const approveAll = async () => {
    if (approvingAll) return;
    // If a brand/search filter is active, only approve the visible slice
    // (per-row PUT). Otherwise use the atomic bulk endpoint for speed.
    const usingFilter = q || brand || approvedFilter;
    const target = staged.filter((s) => !s.approved);
    if (target.length === 0) { toast.info("Nothing to approve"); return; }
    if (!window.confirm(`Approve ${target.length} styles${usingFilter ? " (matching current filter)" : " (ALL staged)"}?`)) return;
    setApprovingAll(true);
    try {
      if (usingFilter) {
        for (const s of target) await ssSetApproval(s.style_id, true, pw).catch(() => {});
      } else {
        const res = await ssApproveAll(pw);
        toast.success(`Approved ${res.approved} styles`);
      }
      await refresh();
    } catch { toast.error("Bulk approve failed"); }
    setApprovingAll(false);
  };

  const doPromote = async () => {
    if (promoting) return;
    const approvedCount = staged.filter((s) => s.approved && !s.promoted).length;
    if (approvedCount === 0) {
      toast.error("No approved styles to promote");
      return;
    }
    if (!window.confirm(`Promote ${approvedCount} approved styles into live products?`)) return;
    setPromoting(true);
    try {
      const res = await ssPromote({ price_mode: priceMode, markup_pct: markupPct, overwrite }, pw);
      toast.success(`Promoted ${res.promoted}, skipped ${res.skipped}`);
      refresh();
    } catch { toast.error("Promote failed"); }
    setPromoting(false);
  };

  const lr = status?.last_run;
  const phaseText = lr?.phase === "done"
    ? `DONE ${lr.styles_done}/${lr.styles_total}`
    : lr?.phase
      ? `${lr.phase.toUpperCase()} ${lr.styles_done || 0}/${lr.styles_total || "?"}`
      : "IDLE";
  const progressPct = lr && lr.styles_total
    ? Math.round(((lr.styles_done || 0) / lr.styles_total) * 100)
    : 0;
  // iter79e — live speed + ETA for the sync (both phases share this widget)
  const active = lr?.phase && !["done", "error"].includes(lr.phase);
  const { speed, etaStr, ready: speedReady } = useSyncSpeed({
    done: lr?.styles_done || 0,
    total: lr?.styles_total || 0,
    active,
  });

  return (
    <div className="space-y-4" data-testid="ss-sync-tab">
      {/* Status header */}
      <div className="border border-[#222] bg-[#0a0a0a] p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-display text-2xl uppercase">S&S Activewear Canada</div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mt-1">
              STAGED {status?.staged_total ?? "…"} · APPROVED {status?.approved ?? "…"} · PROMOTED {status?.promoted ?? "…"}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={doReset}
              data-testid="ss-reset-btn"
              className="border border-[var(--tc-red)] text-[var(--tc-red)] px-3 py-2 font-mono text-[10px] tracking-widest flex items-center gap-1 hover:bg-red-950"
            >
              <Trash2 size={12} /> WIPE ALL
            </button>
            {(lr?.styles_failed || 0) > 0 && (
              <button
                onClick={doRetryFailed}
                disabled={retrying}
                data-testid="ss-retry-failed-btn"
                className="border border-amber-600 text-amber-400 px-3 py-2 font-mono text-[10px] tracking-widest flex items-center gap-1 hover:bg-amber-950 disabled:opacity-50"
                title="Re-fetch the styles that failed in the last run"
              >
                <RefreshCw size={12} /> {retrying ? "Retrying…" : `RETRY ${lr.styles_failed} FAILED`}
              </button>
            )}
            <button
              onClick={doSync}
              disabled={syncing}
              data-testid="ss-sync-btn"
              className="btn-tc-primary py-2 px-4 flex items-center gap-2 disabled:opacity-50"
            >
              <Play size={13} /> {syncing ? "Starting…" : "RUN FULL SYNC"}
            </button>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-3">
          <div className="flex justify-between font-mono text-[10px] tracking-widest text-zinc-500">
            <span>{phaseText}</span>
            <span>{progressPct}%</span>
          </div>
          <div className="h-1 bg-[#222] mt-1 relative overflow-hidden">
            <div className="h-full bg-[var(--tc-red)] transition-all" style={{ width: `${progressPct}%` }} />
          </div>
          {active && (
            <div className="mt-1 flex items-center justify-between font-mono text-[10px] tracking-widest text-zinc-500" data-testid="ss-speed-line">
              <span>{speedReady ? <>~{speed} styles/sec</> : "measuring speed…"}</span>
              <span className="text-zinc-400">ETA {speedReady ? etaStr : "…"}</span>
            </div>
          )}
          {lr?.errors?.length > 0 && (
            <div className="mt-2 font-mono text-[10px] text-amber-400" data-testid="ss-errors">
              {lr.errors.length} error{lr.errors.length === 1 ? "" : "s"} — first: {lr.errors[0].error?.slice(0, 100)}
            </div>
          )}
        </div>
      </div>

      {/* Promote block */}
      <div className="border border-[#222] bg-[#0a0a0a] p-4 space-y-3" data-testid="ss-promote-block">
        <div className="font-mono text-[10px] tracking-widest text-zinc-500">
          / PROMOTE APPROVED → LIVE PRODUCTS
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="font-mono text-[9px] tracking-widest text-zinc-500">PRICE SOURCE</span>
            <select
              value={priceMode}
              onChange={(e) => setPriceMode(e.target.value)}
              data-testid="ss-price-mode"
              className="bg-black border border-[#222] px-3 py-1.5 font-mono text-xs"
            >
              <option value="retail">Retail (2026 catalog)</option>
              <option value="piece">Piece (wholesale)</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-mono text-[9px] tracking-widest text-zinc-500">MARKUP %</span>
            <input
              type="number"
              value={markupPct}
              onChange={(e) => setMarkupPct(parseFloat(e.target.value) || 0)}
              step="0.5"
              data-testid="ss-markup-pct"
              className="w-28 bg-black border border-[#222] px-3 py-1.5 font-mono text-xs"
            />
          </label>
          <label className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-zinc-400">
            <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="ss-overwrite" />
            OVERWRITE EXISTING
          </label>
          <button
            onClick={doPromote}
            disabled={promoting}
            data-testid="ss-promote-btn"
            className="btn-tc-primary py-2 px-4 flex items-center gap-2 disabled:opacity-50"
          >
            <Rocket size={13} /> {promoting ? "Promoting…" : "PROMOTE APPROVED"}
          </button>
        </div>
      </div>

      {/* Filters + approve-all */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search title / style / brand / part#"
          data-testid="ss-search"
          className="flex-1 min-w-[200px] bg-black border border-[#222] px-3 py-2 text-sm font-body outline-none"
        />
        <select
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          data-testid="ss-brand-filter"
          className="min-w-[180px] bg-black border border-[#222] px-3 py-2 font-mono text-xs"
        >
          <option value="">ALL BRANDS ({brandOptions.length})</option>
          {brandOptions.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
        <select
          value={approvedFilter}
          onChange={(e) => setApprovedFilter(e.target.value)}
          data-testid="ss-approved-filter"
          className="bg-black border border-[#222] px-3 py-2 font-mono text-xs"
        >
          <option value="">ALL</option>
          <option value="true">APPROVED</option>
          <option value="false">UNAPPROVED</option>
        </select>
        <button
          onClick={refresh}
          data-testid="ss-refresh"
          className="border border-[#222] hover:border-white px-3 py-2 font-mono text-[10px] tracking-widest flex items-center gap-1"
        >
          <RefreshCw size={11} /> REFRESH
        </button>
        <button
          onClick={approveAll}
          disabled={approvingAll || staged.length === 0}
          data-testid="ss-approve-all"
          className="border border-emerald-700 text-emerald-400 hover:bg-emerald-950 px-3 py-2 font-mono text-[10px] tracking-widest disabled:opacity-50"
        >
          {approvingAll ? "APPROVING…" : `APPROVE ${(q||brand||approvedFilter) ? staged.length + " VISIBLE" : "ALL STAGED"}`}
        </button>
      </div>

      {/* Staging table */}
      <div className="surface-card overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-[#050505] border-b border-[#222]">
            <tr className="font-mono text-[10px] tracking-widest text-zinc-500">
              <th className="p-2 w-10"></th>
              <th className="p-2 w-16">IMG</th>
              <th className="p-2">TITLE</th>
              <th className="p-2">BRAND</th>
              <th className="p-2">STYLE</th>
              <th className="p-2">CAT</th>
              <th className="p-2">COLORS</th>
              <th className="p-2">SKUs</th>
              <th className="p-2">PROMOTED</th>
            </tr>
          </thead>
          <tbody>
            {staged.map((s) => {
              const thumb = (s.colors || [])[0]?.front || s.style_image;
              return (
                <tr key={s.style_id} data-testid={`ss-row-${s.style_id}`} className={`border-b border-[#222] ${s.approved ? "bg-emerald-950/10" : ""}`}>
                  <td className="p-2">
                    <input
                      type="checkbox"
                      checked={!!s.approved}
                      onChange={() => toggleApproval(s.style_id, s.approved)}
                      data-testid={`ss-approve-${s.style_id}`}
                      className="accent-[var(--tc-red)] w-4 h-4"
                    />
                  </td>
                  <td className="p-2">
                    {thumb && <img src={thumb} alt="" className="w-12 h-12 object-cover bg-black" loading="lazy" />}
                  </td>
                  <td className="p-2 font-ui text-xs uppercase truncate max-w-[280px]" title={s.title}>{s.title}</td>
                  <td className="p-2 font-mono text-[10px] text-zinc-300">{s.brand_name}</td>
                  <td className="p-2 font-mono text-[10px] text-zinc-400">{s.style_name}<br /><span className="text-zinc-600">{s.part_number}</span></td>
                  <td className="p-2 font-mono text-[10px] text-zinc-400 truncate max-w-[120px]">{s.base_category}</td>
                  <td className="p-2 font-mono text-xs text-zinc-300">{s.color_count}</td>
                  <td className="p-2 font-mono text-xs text-zinc-300">{s.sku_count}</td>
                  <td className="p-2">
                    {s.promoted
                      ? <CheckCircle2 size={14} className="text-emerald-400" />
                      : <XCircle size={14} className="text-zinc-600" />}
                  </td>
                </tr>
              );
            })}
            {staged.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center font-mono text-xs text-zinc-500">No staged styles. Click RUN FULL SYNC to fetch from S&S.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
